// ONE SETTLE-LATER TRADE, over its list (UI rebuild batch 3 — Astra's `settlement-detail`, as the operator ruled it on
// 26 Oct 2026: no deal-scoped settlement form). It shows what Astra's view keeps visible — the deal reference, the party,
// what is outstanding — read off the settle-later read, plus the party's box in the Books' words; its doors are Record
// money prefilled from each open leg; Mark settled (the guarded override) and Hold leg live here, one tap from the list.
import { Link } from 'react-router-dom';
import type { SettlementParty } from '../../../lib/api';
import { displayAsset, fmtAmount, fmtRate, fmtStamp } from '../../../lib/format';
import { dustFor, partyName } from '../../../lib/naming';
import { legWords } from '../../SettleButton';
import { BoxWords } from '../../BoxLines';
import { CopyRef } from '../../CopyRef';
import { rateQuote } from '../../Ledger';
import { DetailSheet, Fact } from '../kit';
import { HoldLink, SettleDoor, TradeSentence, openLegsOf, sideWords, type Side } from './parts';
import { MarkSettled } from './MarkSettled';

type Deal = SettlementParty['deals'][number];

export function SettleDetail({ party, deal, onClose }: { party: SettlementParty; deal: Deal; onClose: () => void }) {
  const name = partyName({ display_name: party.display_name, slug: party.slug });
  const legs = deal.legs;
  const doors = openLegsOf(legs).filter(({ leg }) => leg.open > 0);
  const box = Object.entries(party.box).filter(([a, v]) => Math.abs(v) > dustFor(a));

  return (
    <DetailSheet
      title={name}
      onClose={onClose}
      footer={doors.length > 0 && (
        <div className="grid gap-2 sm:flex sm:flex-wrap sm:justify-end">
          {doors.map(({ side, leg }) => (
            <SettleDoor key={side} partySlug={party.slug} side={side} leg={leg} withFigure primary={doors.length === 1} />
          ))}
        </div>
      )}
    >
      <Fact label="Deal"><CopyRef value={deal.deal_id} /></Fact>
      <Fact label="Party">
        <Link to={`/parties/${encodeURIComponent(party.slug)}?entity=dubaiotc`} className="btn-link">{name}</Link>
      </Fact>
      <Fact label="Agreed"><span className="font-mono tabular">{fmtStamp(deal.booked_at)}</span></Fact>
      <Fact label="Trade"><TradeSentence d={deal} /> <span className="whitespace-nowrap text-tg-hint">@ {fmtRate(deal.rate, rateQuote(deal))}</span></Fact>
      {legs ? (['take', 'give'] as const).map((side: Side) => (
        <Fact key={side} label={sideWords(side)}>
          {/* An OPEN leg says its figure first, then the muted unit and what is left to do ("52,728 AED to receive" — never
              "AED 52,728"; Astra's audit P3, 1 Nov 2026); a closed one how it closed. */}
          {legs[side].settled
            ? <span className="text-pos">{legWords(legs[side], side)}</span>
            : <span className="whitespace-nowrap text-warn"><span className="font-mono tabular">{fmtAmount(legs[side].open, legs[side].asset)}</span> <span className="text-tg-hint">{displayAsset(legs[side].asset)} {side === 'take' ? 'to receive' : 'to pay'}</span></span>}
          {legs[side].settled && <HoldLink dealId={deal.deal_id} leg={side} />}
        </Fact>
      )) : <Fact label="Legs"><span className="text-tg-hint">Not stated</span></Fact>}
      <Fact label="Balance">
        {box.length === 0
          ? <span className="text-tg-hint">square</span>
          : box.map(([a, v]) => (
            // The figure, then its muted unit (BoxWords says the words and the figure only) — a party holding two
            // currencies must read which is which (Astra, 1 Nov 2026).
            <span key={a} className="block whitespace-nowrap">
              <BoxWords value={v} asset={a} answer /> <span className="text-tg-hint">{displayAsset(a)}</span>
            </span>
          ))}
      </Fact>
      <div className="pt-4">
        <MarkSettled dealId={deal.deal_id} partySlug={party.slug} onDone={onClose} />
      </div>
    </DetailSheet>
  );
}
